import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../services/api';
import { toast } from 'sonner';
import { MapPin, AlertCircle, Calendar, Save, Briefcase } from 'lucide-react';

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Management'
};

const StaffProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    bio: '',
    demographics: {
      date_of_birth: '',
      gender: '',
      ethnicity: ''
    },
    address: {
      street: '',
      city: '',
      state: '',
      zip_code: ''
    },
    emergency_contact: {
      name: '',
      phone: '',
      relationship: ''
    },
    assigned_duties: [],
    duty_rates: []
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/staff/profile/me');
      const data = response.data;
      setProfile({
        bio: data.bio || '',
        demographics: {
          date_of_birth: data.demographics?.date_of_birth || '',
          gender: data.demographics?.gender || '',
          ethnicity: data.demographics?.ethnicity || ''
        },
        address: {
          street: data.address?.street || '',
          city: data.address?.city || '',
          state: data.address?.state || '',
          zip_code: data.address?.zip_code || ''
        },
        emergency_contact: {
          name: data.emergency_contact?.name || '',
          phone: data.emergency_contact?.phone || '',
          relationship: data.emergency_contact?.relationship || ''
        },
        assigned_duties: data.assigned_duties || [],
        duty_rates: data.duty_rates || []
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/staff/profile/me', {
        bio: profile.bio || null,
        demographics: profile.demographics,
        address: profile.address,
        emergency_contact: profile.emergency_contact
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const updateDemographics = (field, value) => {
    setProfile(prev => ({
      ...prev,
      demographics: { ...prev.demographics, [field]: value }
    }));
  };

  const updateAddress = (field, value) => {
    setProfile(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const updateEmergencyContact = (field, value) => {
    setProfile(prev => ({
      ...prev,
      emergency_contact: { ...prev.emergency_contact, [field]: value }
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="My Profile">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Profile">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="staff-profile-page">
        <form onSubmit={handleSave}>
          {/* Assigned Duties (Read Only) */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-500" />
                Assigned Duties & Rates
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">Set by administrator</p>
            </CardHeader>
            <CardContent className="p-4 lg:p-6">
              {profile.assigned_duties.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No duties assigned yet</p>
              ) : (
                <div className="space-y-3">
                  {profile.assigned_duties.map((duty) => {
                    const rate = profile.duty_rates.find(r => r.duty_type === duty);
                    return (
                      <div key={duty} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{DUTY_LABELS[duty] || duty}</span>
                        <span className="text-green-600 font-semibold">
                          {rate ? `${cs()}${rate.hourly_rate.toFixed(2)}/hr` : 'Rate not set'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Demographics */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Demographics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={profile.demographics.date_of_birth}
                    onChange={(e) => updateDemographics('date_of_birth', e.target.value)}
                    data-testid="dob-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select
                    value={profile.demographics.gender}
                    onValueChange={(value) => updateDemographics('gender', value)}
                  >
                    <SelectTrigger data-testid="gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ethnicity</Label>
                  <Input
                    value={profile.demographics.ethnicity}
                    onChange={(e) => updateDemographics('ethnicity', e.target.value)}
                    placeholder="Optional"
                    data-testid="ethnicity-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder="Tell us about yourself..."
                  rows={3}
                  data-testid="bio-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-500" />
                Address Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 space-y-4">
              <div className="space-y-2">
                <Label>Street Address</Label>
                <Input
                  value={profile.address.street}
                  onChange={(e) => updateAddress('street', e.target.value)}
                  placeholder="123 Main Street, Apt 4B"
                  data-testid="street-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={profile.address.city}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    placeholder="Edison"
                    data-testid="city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={profile.address.state}
                    onChange={(e) => updateAddress('state', e.target.value)}
                    placeholder="NJ"
                    data-testid="state-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    value={profile.address.zip_code}
                    onChange={(e) => updateAddress('zip_code', e.target.value)}
                    placeholder="08844"
                    data-testid="zip-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-rose-500" />
                Emergency Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={profile.emergency_contact.name}
                    onChange={(e) => updateEmergencyContact('name', e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="emergency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={profile.emergency_contact.phone}
                    onChange={(e) => updateEmergencyContact('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="emergency-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Relationship</Label>
                  <Input
                    value={profile.emergency_contact.relationship}
                    onChange={(e) => updateEmergencyContact('relationship', e.target.value)}
                    placeholder="Spouse, Parent, etc."
                    data-testid="emergency-relationship-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default StaffProfile;
